import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight, Download } from "lucide-react";
import { company } from "@/data/site";
import { useT, useContent } from "@/i18n";

export default function Footer() {
  const year = new Date().getFullYear();
  const t = useT();
  const { categories } = useContent();

  const navItems = [
    { to: "/", key: "nav.home" },
    { to: "/kurumsal", key: "nav.about" },
    { to: "/hizmetler", key: "nav.services" },
    { to: "/projeler", key: "nav.projects" },
    { to: "/iletisim", key: "nav.contact" },
  ];

  return (
    <footer className="bg-navy-900 text-white/60" data-testid="footer">
      <div>
        <div className="container-kyt py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* brand */}
          <div className="md:col-span-4">
            <img src="/logo-white.png" alt="KYT Yapı Teknolojileri" className="h-14 w-auto mb-6" />
            <p className="text-sm leading-relaxed text-white/50 max-w-xs font-light">
              {t("footer.desc")}
            </p>
            <div className="flex gap-2 mt-7">
              {["ISO 9001", "ISO 14001", "OHSAS 18001"].map((c) => (
                <span key={c} className="font-mono text-[10px] tracking-wide border border-white/15 px-2.5 py-1 text-white/55">
                  {c}
                </span>
              ))}
            </div>
            <a
              href={company.catalog}
              download
              data-testid="footer-catalog"
              className="mt-7 inline-flex items-center gap-2.5 rounded-full border border-white/25 px-5 py-2.5 text-[13px] font-medium text-white transition-colors duration-300 hover:bg-white hover:text-navy"
            >
              <Download className="h-4 w-4" /> {t("common.catalogDownload")}
            </a>
          </div>

          {/* nav */}
          <div className="md:col-span-2">
            <h4 className="text-white text-sm font-semibold mb-4 tracking-wide">{t("footer.menu")}</h4>
            <ul className="space-y-2.5 text-sm">
              {navItems.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="hover:text-white transition-colors">{t(n.key)}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* categories */}
          <div className="md:col-span-3">
            <h4 className="text-white text-sm font-semibold mb-4 tracking-wide">{t("footer.projectCategories")}</h4>
            <ul className="space-y-2.5 text-sm">
              {categories.filter((c) => c.slug !== "all").slice(0, 6).map((c) => (
                <li key={c.slug}>
                  <Link to={`/projeler?kategori=${c.slug}`} className="hover:text-white transition-colors">
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* contact */}
          <div className="md:col-span-3">
            <h4 className="text-white text-sm font-semibold mb-4 tracking-wide">{t("footer.contact")}</h4>
            <ul className="space-y-3.5 text-sm">
              <li className="flex gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-brand-blue shrink-0" />
                <span>{company.address}</span>
              </li>
              <li>
                <a href={`tel:${company.phoneHref}`} className="flex gap-3 items-center hover:text-white transition-colors">
                  <Phone className="h-4 w-4 text-brand-blue shrink-0" /> {company.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${company.email}`} className="flex gap-3 items-center hover:text-white transition-colors">
                  <Mail className="h-4 w-4 text-brand-blue shrink-0" /> {company.email}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-kyt py-5 flex flex-col sm:flex-row items-center justify-between gap-4 text-[12.5px] text-white/50">
          <span className="order-2 sm:order-1 text-center sm:text-left">© {year} {company.legal}. {t("footer.rights")}</span>
          <div className="order-1 sm:order-2 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link to="/kvkk" data-testid="footer-kvkk" className="hover:text-white transition-colors">{t("footer.kvkk")}</Link>
            <Link to="/cerez-politikasi" data-testid="footer-cookies" className="hover:text-white transition-colors">{t("footer.cookies")}</Link>
            <a href={`mailto:${company.email}?subject=${encodeURIComponent(t("footer.quickQuote"))}`} className="inline-flex items-center gap-1 hover:text-white transition-colors">
              {t("footer.quickQuote")} <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
